import { useState } from "react";

import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  useTheme
} from "@mui/material";

import { DefaultPaper, Autocomplete } from "@aclymatepackages/atoms";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { useInternalAdminContext } from "../../../helpers/contexts/internalAdmin";

// Phase 2 (shadow-read) diagnostic: an admin-only view of the exact
// comparison validate-rollups-v2.js already runs from the CLI (v2 vs
// legacy vs a fresh live recompute), for spot-checking a single company
// without a terminal. Read-only -- the backend endpoint performs no writes.
const monthDiffColumns = [
  { key: "month", label: "Month" },
  { key: "expectedValue", label: "Expected" },
  { key: "actualValue", label: "Actual" },
  { key: "delta", label: "Delta" }
];

const entityDiffColumns = [
  { key: "entityId", label: "ID" },
  { key: "month", label: "Month" },
  { key: "expectedValue", label: "Expected" },
  { key: "actualValue", label: "Actual" },
  { key: "delta", label: "Delta" }
];

const bucketDiffColumns = [
  { key: "id", label: "ID" },
  { key: "issue", label: "Issue" }
];

const formatCell = (value) => (typeof value === "number" ? value.toFixed(2) : value);

const DiffTable = ({ title, rows, columns }) => {
  const { palette } = useTheme();

  if (!rows?.length) {
    return (
      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          OK -- no diffs.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" gutterBottom color="error">
        {title} -- {rows.length} diff{rows.length === 1 ? "" : "s"}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <TableCell key={key} style={{ color: palette.text.secondary }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${title}-${index}`}>
              {columns.map(({ key }) => (
                <TableCell key={key}>{formatCell(row[key])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ComparisonResult = ({ result }) => {
  const {
    company,
    passed,
    inconclusive,
    readCompleteness,
    v2VsExpected,
    legacyVsLive,
    vendorBucketDiffs,
    eventBucketDiffs,
    employeeBucketDiffs,
    vendorTotalDiffs,
    eventTotalDiffs,
    employeeTotalDiffs,
    officeTotalDiffs
  } = result;

  return (
    <Box mt={3}>
      {company && (
        <Typography variant="h6" gutterBottom>
          {company.name} ({company.id})
        </Typography>
      )}
      <Box mb={3} display="flex" alignItems="center">
        <Chip
          label={inconclusive ? "INCONCLUSIVE" : passed ? "PASS" : "FAIL"}
          color={inconclusive ? "warning" : passed ? "success" : "error"}
        />
        {inconclusive && (
          <Box ml={2}>
            <Typography variant="body2" color="warning.main">
              A transaction or rollup update landed while this comparison was
              running -- re-run it before trusting this result either way.
            </Typography>
          </Box>
        )}
        {readCompleteness && (
          <Box ml={2}>
            <Typography variant="body2" color="error">
              Read may be incomplete: fetched {readCompleteness.actual}, expected{" "}
              {readCompleteness.expected}.
            </Typography>
          </Box>
        )}
      </Box>

      <DiffTable
        title="rollups-v2 vs expected (company totals)"
        rows={v2VsExpected}
        columns={monthDiffColumns}
      />
      <DiffTable
        title="Vendor bucket presence"
        rows={vendorBucketDiffs}
        columns={bucketDiffColumns}
      />
      <DiffTable
        title="Event bucket presence"
        rows={eventBucketDiffs}
        columns={bucketDiffColumns}
      />
      <DiffTable
        title="Employee bucket presence"
        rows={employeeBucketDiffs}
        columns={bucketDiffColumns}
      />
      <DiffTable
        title="Vendor totals"
        rows={vendorTotalDiffs}
        columns={entityDiffColumns}
      />
      <DiffTable
        title="Event totals"
        rows={eventTotalDiffs}
        columns={entityDiffColumns}
      />
      <DiffTable
        title="Employee totals"
        rows={employeeTotalDiffs}
        columns={entityDiffColumns}
      />
      <DiffTable
        title="Office totals"
        rows={officeTotalDiffs}
        columns={entityDiffColumns}
      />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Legacy vs live (context only -- does not affect pass/fail)
        </Typography>
        {legacyVsLive?.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                {monthDiffColumns.map(({ key, label }) => (
                  <TableCell key={key}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {legacyVsLive.map((row, index) => (
                <TableRow key={`legacy-vs-live-${index}`}>
                  {monthDiffColumns.map(({ key }) => (
                    <TableCell key={key}>{formatCell(row[key])}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="textSecondary">
            OK -- no diffs.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const AdminRollupsV2ComparisonPage = () => {
  const { selectableCompanies } = useInternalAdminContext();
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const companyId = selectedCompany?.id;

  const onCompare = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetchOurApi({
        path: "/admin/compare-rollups-v2",
        method: "POST",
        data: { companyId },
        callback: (res) => res
      });

      if (response?.error) {
        setError(response.message || "Comparison failed.");
        return;
      }

      setResult(response);
    } catch (err) {
      setError(err?.message || "Comparison failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Rollups V2 Comparison
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Compares one company&apos;s rollups-v2 shadow buckets against legacy
        and a fresh live recompute -- read-only, no writes. This is the same
        check validate-rollups-v2.js runs from the CLI.
      </Typography>

      <DefaultPaper style={{ padding: 24, marginTop: 16, maxWidth: 640 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box flexGrow={1}>
            <Autocomplete
              label="Company"
              availableOptions={selectableCompanies}
              value={selectedCompany}
              setValue={setSelectedCompany}
              size="small"
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={onCompare}
            disabled={!companyId || loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Compare"}
          </Button>
        </Box>
      </DefaultPaper>

      {error && (
        <Box mt={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {result && <ComparisonResult result={result} />}
    </Box>
  );
};

export default AdminRollupsV2ComparisonPage;
